/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal } from 'src/core/components/BasicModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { Page } from 'src/core/components/Page';
import { useAuthKeys } from 'src/core/hooks/useAuthKeys';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logButtonClick, logURlLinkClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { DeployedModelsTableCommandBar } from 'src/modules/modelDeploy/DeployedModelsTableCommandBar';
import { DeployedModelUrlModal } from 'src/modules/modelDeploy/DeployedModelUrlModal';
import { QnaMakerProjectDeployedCard } from 'src/qnaMaker/appShell/qnaMakerDeployPage/QnaMakerProjectDeployedCard';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

const messages = defineMessages({
    deploy: { id: 'qna.qnaMakerDeployPage.deploy', defaultMessage: 'Deploy' },
    stepLabel: { id: 'qna.qnaMakerDeployPage.stepLabel', defaultMessage: 'Step {stepNumber}:' },
    createBot: { id: 'qna.qnaMakerDeployPage.createBot', defaultMessage: 'Create a bot' },
    readDocs: { id: 'qna.qnaMakerDeployPage.readDocs', defaultMessage: 'Read the documentation' },
    pageTitle: { id: 'qna.qnaMakerDeployPage.pageTitle', defaultMessage: 'Deploy knowledge base' },
    nextStepsTitle: { id: 'qna.qnaMakerDeployPage.nextStepsTitle', defaultMessage: 'Next steps: Create a bot' },
    stepTwoText: { id: 'qna.qnaMakerDeployPage.stepTwoText', defaultMessage: 'Go to Azure to create a bot.' },
    stepOneText: { id: 'qna.qnaMakerDeployPage.stepOneText', defaultMessage: 'to learn more about creating bots.' },
    deployProjectTitle: { id: 'qna.qnaMakerDeployPage.deployProjectTitle', defaultMessage: 'Deploy this project?' },
    deploymentFailedTitle: { id: 'qna.qnaMakerDeployPage.deploymentFailedTitle', defaultMessage: '{projectName} deployment failed' },
    deploymentCompletedTitle: {
        id: 'qna.qnaMakerDeployPage.deploymentCompletedTitle',
        defaultMessage: '{projectName} - Successfully deployed'
    },
    deploymentInProgressTitle: {
        id: 'qna.qnaMakerDeployPage.deploymentInProgressTitle',
        defaultMessage: '{projectName} deployment in progress'
    },
    deploymentInProgressContent: {
        id: 'qna.qnaMakerDeployPage.deploymentInProgressContent',
        defaultMessage: 'Deployment could take a couple minutes.'
    },
    deployProjectMessage: {
        id: 'qna.qnaMakerDeployPage.deployProjectMessage',
        defaultMessage: 'Deploy {projectName}? Once deployed you can integrate it within a bot.'
    },
    pageDescription: {
        id: 'qna.qnaMakerDeployPage.pageDescription',
        defaultMessage: 'Deploy knowledge base and create a bot in a few clicks.'
    },
    emptyMessage: {
        id: 'qna.qnaMakerDeployPage.emptyMessage',
        defaultMessage: `Deploying your knowledge base will copy the knowledge base
        from the test index to the production index.`
    },
    moonCakeDisabledBotMsg: {
        id: 'qna.qnaMakerDeployPage.moonCakeDisabledBotMsg',
        defaultMessage:
            'We are not yet enabling creation of bot in Mooncake as Bot Service is not yet available in Mooncake. Please note that we will enable as soon as it is available.'
    }
});

const getQnaMakerTestBody = (enable: boolean) => ({
    top: 3,
    question: 'YOUR_QUESTION_HERE',
    includeUnstructuredSources: true,
    confidenceScoreThreshold: 'YOUR_SCORE_THRESHOLD_HERE',
    answerSpanRequest: { enable, topAnswersWithSpan: 1, confidenceScoreThreshold: 'YOUR_SCORE_THRESHOLD_HERE' },
    filters: {
        metadataFilter: {
            logicalOperation: 'YOUR_LOGICAL_OPERATION_HERE',
            metadata: [{ key: 'YOUR_ADDITIONAL_PROP_KEY_HERE', value: 'YOUR_ADDITIONAL_PROP_VALUE_HERE' }]
        }
    }
});

const ToastContentComponent = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { currentQnaMakerProjectStore } = rootStore;
    const { publishStatus } = currentQnaMakerProjectStore;

    if (publishStatus !== 'succeeded') {
        return <LuisText>{localize(messages.deploymentInProgressContent)}</LuisText>;
    }

    return null;
});

export const QnaMakerDeployPage = observer(() => {
    const tracker = useProgressTracker();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { dispatcher, rootStore, uiStore } = useGlobalState();
    const { currentQnaMakerProjectStore } = rootStore;
    const { lastPublishedDate: lastProjectPublishedDate, name: projectName, id: currentProjectId } = currentQnaMakerProjectStore;
    const isMoonCake = window.location.hostname === LuisConstants.LanguageStudio.MoonCakeHostName;

    const [modalStates, updateModalStates] = useUpdateState({ isDeployModalHidden: true, isEndpointURLModalHidden: true });

    const isInEmptyState = !lastProjectPublishedDate;

    const currentServiceKey = rootStore.azureStore.session.serviceKey;
    const currentResource = rootStore.azureStore.session.csAccountStore;
    const isProjectDeployed = Boolean(currentQnaMakerProjectStore.lastPublishedDate);

    const { authorizationHeader } = useAuthKeys({ currentServiceKey, currentResource });

    const endpointUrl =
        `${currentResource?.endpoint ?? 'https://YOUR_RESOURCE_ENDPOINT/'}language` +
        `/:query-knowledgebases?projectName=${projectName}&api-version=${LuisConstants.QnaMaker.ApiVersion}` +
        `&deploymentName=${isProjectDeployed ? 'production' : 'test'}`;

    const submitCode = `curl -X POST "${endpointUrl}" -H "${authorizationHeader.key}: ${authorizationHeader.value ??
        'YOUR_ENDPOINT_KEY'}" -H "Content-Type: application/json" -d "${JSON.stringify(getQnaMakerTestBody(isProjectDeployed))
        .split('"')
        .join('\\"')}"`;

    const [isDeploymentFailed, updateIsDeploymentFailed] = React.useState(false);

    const createBotButtonIdRef = React.useRef<string>(getId());

    React.useEffect(() => () => dequeueTeachingBubble('QnaCreateBot'), []);

    React.useEffect(() => {
        if (!isInEmptyState && !uiStore.teachingBubbles.QnaCreateBot) {
            createTeachingBubble(
                'QnaCreateBot',
                localize(qnaMakerTeachingBubbleMessages.createBotBody),
                localize(qnaMakerTeachingBubbleMessages.createBotTitle),
                `#${createBotButtonIdRef.current}`,
                true,
                true
            );
        }
    }, [isInEmptyState, Object.values(uiStore.teachingBubbles)]);

    React.useEffect(() => {
        if (tracker.state === 'completed') {
            updateIsDeploymentFailed(false);
        } else if (tracker.state === 'failed') {
            updateIsDeploymentFailed(true);
        }
    }, [tracker.state]);

    const onDeploy = () => {
        updateModalStates({ isDeployModalHidden: true });
        dispatcher.dispatch('deployQnAMakerProject', {
            projectId: projectName,
            tracker,
            toastData: {
                failed: { title: localize(messages.deploymentFailedTitle, { projectName }) },
                completed: { title: localize(messages.deploymentCompletedTitle, { projectName }) },
                running: { title: localize(messages.deploymentInProgressTitle, { projectName }), content: <ToastContentComponent /> }
            }
        });

        logButtonClick(serviceName, 'DeployModel', 'SubmitDeployment', null, currentProjectId);
    };

    const createChatBot = () => {
        logButtonClick(serviceName, 'DeployModel', 'CreateBot', null, currentProjectId);
        let url = `https://portal.azure.com/#create/Microsoft.Template/uri/https%3A%2F%2Fdev.botframework.com%2Fapi%2Fcustom%2Ftemplate/createUIDefinitionUri/https%3A%2F%2Fdev.botframework.com%2Fapi%2Fcustom%2FcreateUiDefinition%3FMicrosoft_Azure_BotService_subscriptionId%3D8d5ceb22-44c7-4367-bfc6-07378096e055%26Microsoft_Azure_BotService_projectName%3D${projectName}%26Microsoft_Azure_BotService_csResourceGroupName%3D${currentResource?.resourceGroupName}%26Microsoft_Azure_BotService_csAccountName%3D${currentResource?.name}`;
        switch (window.location.hostname) {
            case LuisConstants.LanguageStudio.FairFaxHostName:
                url = `https://portal.azure.us/#create/Microsoft.Template/uri/https%3A%2F%2Fdev.botframework.com%2Fapi%2Fcustom%2Ftemplate/createUIDefinitionUri/https%3A%2F%2Fdev.botframework.com%2Fapi%2Fcustom%2FcreateUiDefinition%3FMicrosoft_Azure_BotService_subscriptionId%3D8d5ceb22-44c7-4367-bfc6-07378096e055%26Microsoft_Azure_BotService_projectName%3D${projectName}%26Microsoft_Azure_BotService_csResourceGroupName%3D${currentResource?.resourceGroupName}%26Microsoft_Azure_BotService_csAccountName%3D${currentResource?.name}`;
                break;
            case LuisConstants.LanguageStudio.MoonCakeHostName:
                url = `https://portal.azure.cn/#create/Microsoft.Template/uri/https%3A%2F%2Fdev.botframework.com%2Fapi%2Fcustom%2Ftemplate/createUIDefinitionUri/https%3A%2F%2Fdev.botframework.com%2Fapi%2Fcustom%2FcreateUiDefinition%3FMicrosoft_Azure_BotService_subscriptionId%3D8d5ceb22-44c7-4367-bfc6-07378096e055%26Microsoft_Azure_BotService_projectName%3D${projectName}%26Microsoft_Azure_BotService_csResourceGroupName%3D${currentResource?.resourceGroupName}%26Microsoft_Azure_BotService_csAccountName%3D${currentResource?.name}`;
                break;
            default:
        }
        window.open(url);
    };

    const handleDeploy = () => {
        logButtonClick(serviceName, 'DeployModel', 'Deploy', null, currentProjectId);
        updateModalStates({ isDeployModalHidden: false });
    };

    const handleGetPredictionUrl = () => {
        logButtonClick(serviceName, 'DeployModel', 'PredictionUrl', null, currentProjectId);
        updateModalStates({ isEndpointURLModalHidden: false });
    };

    const handleCancelDeployment = () => {
        logButtonClick(serviceName, 'DeployModel', 'CancelDeployment', null, currentProjectId);
        updateModalStates({ isDeployModalHidden: true });
    };

    return (
        <Page size="container-fluid" pageName="QnaMakerDeployPage" title={localize(messages.pageTitle)}>
            <Stack verticalFill>
                <EditableTitle title={localize(messages.pageTitle)} subtitle={localize(messages.pageDescription)} />

                <DeployedModelsTableCommandBar
                    onDeploy={handleDeploy}
                    includeSearchBar={false}
                    deployMessage={localize(messages.deploy)}
                    onGetPredictionUrl={handleGetPredictionUrl}
                    noneKeys={['deployModel', 'getPredictionURL']}
                />

                {isInEmptyState ? (
                    <EmptyStateView message={localize(messages.emptyMessage)} />
                ) : (
                    <Stack tokens={{ childrenGap: 20 }}>
                        <QnaMakerProjectDeployedCard isDeploymentFailed={isDeploymentFailed} />

                        <Stack horizontalAlign="start" tokens={{ childrenGap: 10 }}>
                            <LuisText fontWeight={600} variant="mediumPlus">
                                {localize(messages.nextStepsTitle)}
                            </LuisText>

                            <Stack tokens={{ childrenGap: 16 }}>
                                <LuisText variant="mediumPlus">
                                    {localize(messages.stepLabel, { stepNumber: 1 })}
                                    &nbsp;
                                    <ExternalLink
                                        href="https://aka.ms/qna-create-bot"
                                        onClick={() => logURlLinkClick(serviceName, 'deployModel', 'readDocumentation', currentProjectId)}>
                                        {localize(messages.readDocs)}
                                    </ExternalLink>
                                    &nbsp;
                                    {localize(messages.stepOneText)}
                                </LuisText>
                                <LuisText variant="mediumPlus">
                                    {localize(messages.stepLabel, { stepNumber: 2 })}
                                    &nbsp;
                                    {localize(messages.stepTwoText)}
                                </LuisText>
                            </Stack>

                            <LuisTooltip hidden={!isMoonCake} content={localize(messages.moonCakeDisabledBotMsg)}>
                                <PrimaryButton
                                    disabled={isMoonCake}
                                    onClick={createChatBot}
                                    id={createBotButtonIdRef.current}
                                    text={localize(messages.createBot)}
                                    styles={{ root: { marginTop: '26px !important' } }}
                                />
                            </LuisTooltip>
                        </Stack>
                    </Stack>
                )}
            </Stack>

            <BasicModal
                valid
                onSubmit={onDeploy}
                data-automation-id="deployModal"
                onDismiss={handleCancelDeployment}
                hidden={modalStates.isDeployModalHidden}
                doneButtonText={localize(messages.deploy)}
                dialogContentProps={{
                    title: localize(messages.deployProjectTitle),
                    subText: localize(messages.deployProjectMessage, { projectName })
                }}>
                <Announced aria-live="assertive" message={localize(messages.deployProjectMessage, { projectName })} />
            </BasicModal>

            <DeployedModelUrlModal
                valid
                predictUrl={endpointUrl}
                submitCode={submitCode}
                hidden={modalStates.isEndpointURLModalHidden}
                onDismiss={() => updateModalStates({ isEndpointURLModalHidden: true })}
            />
        </Page>
    );
});
